import React, { useState, useRef, useEffect } from 'react';
import { Video } from 'lucide-react';

interface EquipementFilterSectionProps {
  title: string;
  items: string[];
  selected: string[];
  onToggle: (item: string) => void;
  disabled?: boolean;
}

const equipmentTypes: Record<string, string> = {
  Audio: 'Microphone',
  Basler: 'Camera',
  ZedX: 'Camera',
  Navtech: 'Radar',
  RoboSense: 'Lidar',
  Leishen: 'Lidar',
};

export default function Equipment({
  title,
  items,
  selected,
  onToggle,
  disabled = false,
}: EquipementFilterSectionProps) {
  const [selectedZedXOption, setSelectedZedXOption] = useState<string[]>([
    'Left',
    'Right',
  ]);
  const zedXButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const event = new CustomEvent('zedx-select', {
      detail: selectedZedXOption,
    });
    window.dispatchEvent(event);
  }, [selectedZedXOption]);

  const toggleZedXSide = (side: 'Left' | 'Right') => {
    if (disabled) return;
    setSelectedZedXOption((prev) => {
      const newState = prev.includes(side)
        ? prev.filter((s) => s !== side)
        : [...prev, side];
      if (newState.length === 0) {
        setTimeout(() => {
          onToggle('ZedX');
        }, 0);
      }
      return newState;
    });
  };

  const onToggleButton = (item: any) => {
    if (disabled) return;
    if (item !== 'ZedX') {
      onToggle(item);
    } else {
      if (selectedZedXOption.length < 1) {
        setSelectedZedXOption(['Left', 'Right']);
      }
      onToggle(item);
    }
  };

  let icon;
  switch (title) {
    case 'Sensors':
      icon = <Video className="inline-block w-5 h-5 mr-2 text-purple-500" />;
      break;
    default:
      icon = null;
  }

  const isMetadataOnly = selected.includes('No Sensor');

  return (
    <div
      className={`space-y-2 text-center ${
        disabled ? 'opacity-50 pointer-events-none' : ''
      }`}
    >
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-300 dark:border-gray-600 pb-1 inline-flex items-center justify-center gap-2">
        {icon} {title}
      </h3>
      {/* No Sensor — metadata only toggle */}
      {items.includes('No Sensor') && (
        <div className="flex justify-center mb-4">
          <button
            onClick={() => onToggleButton('No Sensor')}
            disabled={disabled}
            className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-sm font-medium transition ${
              selected.includes('No Sensor')
                ? 'border-slate-400 bg-slate-200 text-slate-700 dark:bg-slate-500 dark:border-slate-500 dark:text-white shadow-sm'
                : 'border-dashed border-gray-400 dark:border-gray-500 text-gray-500 dark:text-gray-400 hover:border-slate-500 hover:text-slate-600 dark:hover:text-slate-300'
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-3.5 h-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
            </svg>
            No sensor files (metadata and default files only)
          </button>
        </div>
      )}

      <div
        className={`flex flex-wrap justify-center gap-2 mt-6 transition-all duration-300 ${isMetadataOnly ? 'hidden' : ''}`}
      >
        {items && Array.isArray(items) ? (
          (() => {
            const filteredItems = items.filter((item) => item !== 'No Sensor');
            let i = 0;
            const elements = [];

            while (i < filteredItems.length) {
              const currentType = equipmentTypes[filteredItems[i]] || null;
              let count = 1;
              while (
                i + count < filteredItems.length &&
                (equipmentTypes[filteredItems[i + count]] || null) ===
                  currentType
              ) {
                count++;
              }

              const isResetGroup = currentType === null;
              const showSeparatorAfter =
                isResetGroup && i + count < filteredItems.length;

              elements.push(
                <React.Fragment key={`group-${i}`}>
                  <div className="flex flex-col items-center gap-1">
                    {currentType ? (
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {currentType}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        &nbsp;
                      </span>
                    )}

                    <div className="flex gap-2">
                      {filteredItems.slice(i, i + count).map((item) => {
                        const isZedX = item === 'ZedX';
                        const isResetItem = item === 'All';
                        const isSelected =
                          selected.includes(item) ||
                          (isZedX &&
                            selected.includes('Stereo') &&
                            !selected.includes('All'));

                        return (
                          <div
                            key={item}
                            className="flex flex-col items-center"
                          >
                            <button
                              ref={isZedX ? zedXButtonRef : null}
                              onClick={() => onToggleButton(item)}
                              disabled={disabled}
                              className={`px-4 py-2 rounded transition font-medium ${
                                disabled
                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                  : isSelected
                                    ? isResetItem
                                      ? 'bg-blue-500 text-white hover:bg-blue-400 dark:hover:bg-blue-400'
                                      : 'bg-green-500 text-white hover:bg-green-400 dark:hover:bg-green-400'
                                    : 'bg-gray-200 dark:bg-gray-700 text-black dark:text-white hover:bg-green-400 dark:hover:bg-green-400'
                              }`}
                              style={isZedX ? { width: 120 } : undefined}
                            >
                              {item}
                            </button>

                            {isZedX && isSelected && !disabled && (
                              <div
                                className="mt-1 flex gap-0"
                                style={{ width: 120 }}
                              >
                                {['Left', 'Right'].map((side) => (
                                  <button
                                    key={side}
                                    onClick={() =>
                                      toggleZedXSide(side as 'Left' | 'Right')
                                    }
                                    disabled={disabled}
                                    className={`px-2 py-1 rounded transition font-medium ${
                                      selectedZedXOption.includes(side)
                                        ? 'bg-green-500 text-white hover:bg-green-400 dark:hover:bg-green-400'
                                        : 'bg-gray-200 dark:bg-gray-700 text-black dark:text-white hover:bg-green-400 dark:hover:bg-green-400'
                                    }`}
                                    style={{ width: 70 }}
                                  >
                                    {side}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {showSeparatorAfter && (
                    <span className="self-center text-gray-300 dark:text-gray-600 select-none text-xl leading-none mt-5">
                      |
                    </span>
                  )}
                </React.Fragment>
              );

              i += count;
            }
            return elements;
          })()
        ) : (
          <p>No items available</p>
        )}
      </div>
    </div>
  );
}
